import { useEffect, useState } from "react";
import { Mail, ArrowUpRight } from "lucide-react";

// Email parts are stored separately so the raw address never appears in
// statically-rendered HTML. Assembled at runtime in useEffect.
const USER = "kontakt";
const DOMAIN = "grayscale-media";
const TLD = "de";

function assemble() {
  return `${USER}\u0040${DOMAIN}\u002E${TLD}`;
}

/**
 * ProtectedEmail
 * Never embeds the raw "kontakt@grayscale-media.de" string in HTML.
 * Variants:
 *   - "button"  : Pill button "Kontakt aufnehmen" + mail icon
 *   - "inline"  : Clickable text that reveals the address after JS hydration
 *   - "card"    : The styled contact-section email card
 */
export default function ProtectedEmail({
  variant = "inline",
  className = "",
  label,
  testId = "protected-email",
}) {
  const [email, setEmail] = useState("");

  useEffect(() => {
    setEmail(assemble());
  }, []);

  const open = (e) => {
    e.preventDefault();
    if (!email) return;
    window.location.href = `mailto:${email}`;
  };

  if (variant === "button") {
    return (
      <button
        type="button"
        onClick={open}
        data-testid={testId}
        data-cursor="hover"
        className={
          className ||
          "group inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-black font-medium text-sm hover:bg-zinc-200 transition-all duration-300"
        }
        aria-label="Kontakt per E-Mail aufnehmen"
      >
        <Mail className="w-4 h-4" />
        <span>{label || "Kontakt aufnehmen"}</span>
        <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </button>
    );
  }

  if (variant === "card") {
    return (
      <div
        data-testid={testId}
        className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6"
      >
        <div className="flex items-center gap-3">
          <Mail className="w-4 h-4 text-blue-300" />
          <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">
            E-Mail
          </div>
        </div>
        <button
          type="button"
          onClick={open}
          data-cursor="hover"
          data-testid={`${testId}-button`}
          aria-label="E-Mail-Adresse öffnen"
          className="mt-3 group inline-flex items-center gap-2 font-display text-lg text-white hover:text-blue-200 transition-colors"
        >
          <span aria-hidden={!email}>
            {email || "kontakt [at] grayscale-media [.] de"}
          </span>
          <ArrowUpRight className="w-4 h-4 opacity-60 group-hover:opacity-100 transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </button>
        <p className="mt-2 text-sm text-zinc-500">
          Antwort meist innerhalb von 24 Stunden.
        </p>
      </div>
    );
  }

  // "inline"
  return (
    <a
      href="#"
      onClick={open}
      data-testid={testId}
      data-cursor="hover"
      aria-label="E-Mail-Adresse öffnen"
      className={
        className ||
        "underline underline-offset-2 decoration-zinc-600 hover:decoration-white hover:text-white transition-colors"
      }
    >
      {email || "kontakt [at] grayscale-media [.] de"}
    </a>
  );
}
